import { getPublishedResultPresentation } from './resultEventPresentation.js';
import { recordFieldValue } from './recordFieldValue.js';
import { recordInputDomain, recordFieldMaximum } from './recordInputSchema.js';
import { numericRecordTotal, numericTotalText } from './numericTotal.js';

function titleCase(value) {
  return String(value || '').replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatValue(value, field) {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) return value.map(item => formatValue(item, field)).filter(Boolean).join(', ') || null;
  const option = field?.options?.find(candidate => candidate.value === value);
  if (option?.label) return option.label;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return typeof value === 'object' ? null : String(value);
}

/** Saved choice-writer shapes, independent of a Domain's name or current labels. */
export function legacyEventValues(event) {
  if (event?.isAutomated === true && event?.automatedType === 'weekly_summary' && event?.value === 'Discipline') return ['Good Behaviour'];
  const values = Array.isArray(event?.values)
    ? event.values.map(item => typeof item === 'string' ? item : item?.name).filter(value => typeof value === 'string' && value.trim())
    : [];
  if (values.length) return [...new Set(values)];
  const value = event?.valueName || (event?.value !== event?.valueId ? event?.value : undefined);
  return value === null || value === undefined || value === '' || typeof value === 'object' ? [] : [value];
}

function declaredFields(domain) {
  return [domain?.primary, ...(domain?.followUp || []), ...(domain?.fields || [])].filter(Boolean);
}

/** A declared Reason remains a labelled answer; legacy free reasons are notes. */
export function getDomainEventNote(event, domain) {
  domain = recordInputDomain(event, domain);
  const note = event?.notes || event?.note;
  if (note) return String(note);
  if (!declaredFields(domain).some(field => field.id === 'reason') && typeof event?.reason === 'string') return event.reason;
  return '';
}

export function getDomainEventLabel(event, domain) {
  domain = recordInputDomain(event, domain);
  const publication = getPublishedResultPresentation(event);
  if (publication) return publication.label;
  const primary = domain?.primary;
  const primaryValue = primary?.id ? recordFieldValue(event, primary.id, domain) : undefined;
  const formattedPrimary = formatValue(primaryValue, primary);
  if (formattedPrimary !== null) {
    const maximum = recordFieldMaximum(event, primary?.id, domain);
    return primary?.type === 'number' && maximum != null
      ? `${formattedPrimary} / ${maximum}` : formattedPrimary;
  }
  const typeLabel = formatValue(event?.typeLabel);
  if (typeLabel !== null) return typeLabel;
  const legacyLabel = formatValue(legacyEventValues(event));
  if (legacyLabel !== null) return legacyLabel;
  const numeric = formatValue(event?.numericValue);
  if (numeric !== null) return event?.maxScore != null ? `${numeric} / ${event.maxScore}` : numeric;
  return titleCase(domain?.terminology?.event?.label || domain?.label || 'event');
}

export function getDomainEventDetailRows(event, domain, { includePrimary = false } = {}) {
  domain = recordInputDomain(event, domain);
  const publication = getPublishedResultPresentation(event);
  if (publication) return includePrimary
    ? [{ id: 'published-result', label: 'Result', value: publication.label }, ...publication.details]
    : publication.details;
  const fields = declaredFields(domain);
  const byId = new Map(fields.map(field => [field.id, field]));
  const primaryId = domain?.primary?.id;
  const seen = new Set(primaryId && !includePrimary ? [primaryId] : []);
  const rows = [];
  for (const field of fields) {
    if (!field.id || seen.has(field.id)) continue;
    seen.add(field.id);
    const value = formatValue(recordFieldValue(event, field.id, domain), field);
    const maximum = field.type === 'number' ? recordFieldMaximum(event, field.id, domain) : null;
    if (value !== null) rows.push({ id: field.id, label: field.label || titleCase(field.id),
      value: event.inputSchema && maximum != null ? `${value} / ${maximum}` : value });
  }
  for (const [fieldId, rawValue] of Object.entries(event?.fieldValues || {})) {
    if (seen.has(fieldId)) continue;
    const field = byId.get(fieldId);
    const value = formatValue(rawValue, field);
    if (value !== null) rows.push({ id: fieldId, label: field?.label || titleCase(fieldId), value });
  }
  const total = numericRecordTotal(event, domain);
  if (total) rows.push({ id: '__numericTotal', label: 'Combined total', value: numericTotalText(total) });
  return rows;
}

export function getDomainEventSearchText(event, domain) {
  const details = getDomainEventDetailRows(event, domain);
  return [event?.studentName, getDomainEventLabel(event, domain), getDomainEventNote(event, domain),
    event?.issuedByName, event?.teacherName, event?.createdByName, event?.className,
    ...details.flatMap(row => [row.label, row.value]),
  ].filter(Boolean).join(' ');
}
